import { api } from './client';
import type { ApiResponse } from './types';
import type { EventSummary } from './events';

/**
 * What an event says about itself, and where it is held: the facts a list
 * can be narrowed by, the kind of gathering, and the fields that carry them.
 */
export interface EventFinderParams {
  /** Fact codes from GET /events/facets; every one must hold. At most 5. */
  facets?: string[];
  /** One code from GET /events/gathering-kinds. */
  gatheringKind?: string;
}

export const findEvents = (params: EventFinderParams) =>
  api.get<ApiResponse<{ events: (EventSummary & { gatheringKind: string | null })[] }>>('/events', {
    facets: params.facets?.length ? params.facets.join(',') : undefined,
    gatheringKind: params.gatheringKind || undefined,
    limit: 30,
  });

export interface GatheringKind {
  code: string;
  label: string;
  defaultCategoryCode: string;
  worship: boolean;
}

export const listGatheringKinds = () =>
  api.get<ApiResponse<{ kinds: GatheringKind[] }>>('/events/gathering-kinds');

export interface CatalogQuestion {
  code: string;
  heading: string;
  sheet: 'before_you_go' | 'good_to_know';
  single: boolean;
  inPersonOnly: boolean;
  ownWords: string | null;
  position: 'asked' | 'another';
  followUpOf: string | null;
  answers: Array<{
    code: string;
    label: string;
    sets: { facets?: string[]; field?: { name: string; value: string } };
  }>;
}

export const getQuestionCatalog = (kind: string) =>
  api.get<ApiResponse<{ kind: string; questions: CatalogQuestion[] }>>('/events/question-catalog', { kind });

/** Upcoming events of any realm held for a festival, as its Utsav page lists them. */
export const getFestivalEvents = (slug: string) =>
  api.get<ApiResponse<{ events: EventSummary[] }>>(`/utsav/festival/${slug}`);

/** A festival ahead for the organiser's desk, with how the host's last one went. */
export interface SeasonFestival {
  code: string;
  slug: string;
  name: string;
  festivalDate: string;
  religion: string;
  lastTime: {
    eventId: string;
    title: string;
    eventDate: string;
    went: number;
    avgRating: number | null;
    feedbackCount: number;
  } | null;
}

export const getHostingSeason = (limit = 3) =>
  api.get<ApiResponse<{ festivals: SeasonFestival[] }>>('/users/me/hosting/season', { limit });

/** An institution the caller may host as, with who publishing would tell. */
export interface HostInstitution {
  id: string;
  name: string;
  followerCount: number;
  /** Followers with event notices on: who publishing tells. */
  eventFollowerCount: number;
  can: { createEvents: boolean };
}

export const listHostInstitutions = () => api.get<ApiResponse<HostInstitution[]>>('/institutions/mine');

/** A town a host can pick, with the clock an event there keeps. */
export interface TownSuggestion {
  id: string;
  name: string;
  stateCode: string;
  label: string;
  /** IANA, from the town's state. Send it as the event's `timezone`. */
  timeZone: string | null;
}

export const suggestTowns = (q: string) =>
  api.get<ApiResponse<{ cities: TownSuggestion[] }>>('/waitlist/cities', { q, limit: 8 });

/** A place's standing rule for visitors. */
export interface VisitorRule {
  topic: string;
  text: string;
}

export const VISITOR_RULE_TOPICS = ['shoes', 'dress', 'head_covering', 'photos', 'parking', 'outside_food', 'other'] as const;

export const readVisitorRules = (institutionId: string) =>
  api.get<ApiResponse<{ id: string; name: string; visitorRules: VisitorRule[] }>>(`/institutions/${institutionId}`);

/** Replaces the whole list (at most 12); an empty list removes every rule. */
export const saveVisitorRules = (institutionId: string, visitorRules: VisitorRule[]) =>
  api.patch<ApiResponse<{ visitorRules: VisitorRule[] }>>(`/institutions/${institutionId}`, { visitorRules });

/** The fields of an event this area writes, as they read back on the detail. */
export interface EventAbout {
  id: string;
  realm: 'community' | 'worship';
  gatheringKind: string | null;
  /** HH:MM on the event's own clock. */
  lastEntryTime: string | null;
  /** Where it is held, with the place's rules. Null when none, or the place is not live. */
  heldAt: { id: string; name: string; slug: string; city: string; state: string; visitorRules: VisitorRule[] } | null;
  /** One year's festival code, as `holi-2027`, on any realm. */
  festivalCode: string | null;
  /** public (listed), link (opens by its address) or circle (one circle's). */
  visibility: string;
  circleId: string | null;
  /** everyone, or going: the street address is kept for people going. */
  addressVisibility: string;
  /** False when this viewer is not shown the street address. */
  addressShown: boolean;
}

export const readEventAbout = (eventId: string) => api.get<ApiResponse<EventAbout>>(`/events/${eventId}`);

/** The fields an edit sends. An absent field is left as it is; null clears it. */
export interface EventAboutPatch {
  gatheringKind?: string | null;
  lastEntryTime?: string | null;
  heldAtInstitutionId?: string | null;
  festivalCode?: string | null;
  visibility?: 'public' | 'link' | 'circle';
  circleId?: string | null;
  addressVisibility?: 'everyone' | 'going';
}

export const editEventAbout = (eventId: string, patch: EventAboutPatch) =>
  api.patch<ApiResponse<EventAbout>>(`/events/${eventId}`, patch);
